import React, { useContext, useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { toast } from "react-toastify";
import axios from "../../api/axios";
import { loginRoute, sendConfirmationRoute } from "../../api/routes";
import FormField from "../../components/forms/FormField/FormField";
import { AuthContext } from "../../contexts/AuthContext";
import "./Login.scss";
import DebugLogger from "../../helpers/DebugLogger";
import { assets } from "../../assets/assets";
import animateReveal from "../../helpers/animateText";
import Register from "./Register";
import ConfirmLogin from "./ConfirmLogin";
import ForgottenPassword from "./ForgottenPassword";
import ResetPassword from "./ResetPassword";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock } from "@fortawesome/free-solid-svg-icons";
import { pullInAccountSettings } from "../../helpers/AccountTypes";
import config from "../../config";

const Login = (props) => {
  const location = useLocation();
  const useQuery = new URLSearchParams(useLocation().search);

  const resetToken = useQuery.get("resettoken");
  const email = useQuery.get("email");
  let resetEmail = (resetToken) ? email : undefined;
  let registerEmail = (!resetToken) ? email: undefined;

  useEffect(() => {
    if(resetToken || email) {
      window.history.replaceState(null, document.title, "/");
    }
  }, [resetToken, email])

  useEffect(() => {
    const vid = document.getElementById('twitchDemoVid');
    if (vid && typeof vid.play === 'function') vid.play().catch(()=>{});
  }, [])

  const getRememberMeCookie = () => {
    try {
      const match = document.cookie.match(/(?:^|; )rememberMe=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  };

  const savedRememberEmail = getRememberMeCookie();

  const [showRegisterForm, setShowRegisterForm] = useState((registerEmail) ? true : false);
  const [showForgottenPasswordForm, setShowForgottenPasswordForm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState((resetToken) ? true : false);
  const [showConfirmLoginPopup, setShowConfirmLoginPopup] = useState(props.showConfirmLoginPopup || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState(savedRememberEmail || "");
  const [rememberMe, setRememberMe] = useState(!!savedRememberEmail);
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [authContext, setAuthContext] = useContext(AuthContext)
  const welcomeRef = React.useRef(null);
  const [logoPhase, setLogoPhase] = useState(''); // '', 'visible', 'risen'
  const [panelsPhase, setPanelsPhase] = useState(''); // '', 'visible', 'risen'

  // Build Twitch auth URL in a way that avoids mixed-content and respects
  // the current page protocol/host when possible.
  const twitchAuthHref = (() => {
    try {
      const apiUrl = new URL(config.apiBaseUrl, window.location.origin);
      // Use the current page protocol to avoid mixed-content blocking
      apiUrl.protocol = window.location.protocol;
      // Ensure no trailing slash then append path
      apiUrl.pathname = apiUrl.pathname.replace(/\/$/, '') + '/twitchapi/auth';
      return apiUrl.toString();
    } catch (e) {
      return `${window.location.origin}/twitchapi/auth`;
    }
  })();

  const handleValidation = (event) => {
    let formIsValid = true;
    if(username === '') {
      setUsernameError("Please enter a username"); 
      formIsValid = false;
    } else {
      setUsernameError(""); 
    }
    if(password === ''){
      setPasswordError("Please enter a password"); 
      formIsValid = false;
    }  else {
      setPasswordError("");
    }
    return formIsValid;
  };

  const loginFormSubmitHandler = (e) => {
    e.preventDefault();
    pullInAccountSettings(1000, true);
    setIsSubmitting(true);

    // Persist or clear the rememberMe cookie based on checkbox state
    if (rememberMe && username) {
      const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `rememberMe=${encodeURIComponent(username)}; expires=${expires}; path=/; SameSite=Lax`;
    } else {
      document.cookie = 'rememberMe=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    }

    let formValidationPassed = handleValidation();
    if (formValidationPassed) {
      DebugLogger.log('Attempting login ...')
      axios
        .post(loginRoute, {username:username, password:password, withCredentials:true})
        .then(function (response) {
          setIsSubmitting(false);
          toast.success("Logged In Successfully!");

          const data = response.data
          let skipConfirm = data.globalUserData.ignoreTwoFactor
          setAuthContext((oldValues) => {
            axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
            return { ...oldValues, token:data.token, isLoggedIn:true, userData:data.globalUserData, isConfirmed:skipConfirm}
          })
          if(!response.data.ignore2FA)
          {
            setShowConfirmLoginPopup(true); 
            return axios.post(sendConfirmationRoute, {withCredentials:true})
          }
          else return({data:{success:true}})

        })
        .then(confirmationSent => {
          if(confirmationSent.data.success) {
            toast.success(confirmationSent.data.message)
          } else {
            toast.error(new Error("Send Email Failed - " + confirmationSent.data.message))
          }
        })
        .catch(function (error) {
          setIsSubmitting(false);
          toast.error('Login Failed - Invalid username or password');
      });
    }
    else
    {
      setIsSubmitting(false);
    }
  };

  const toggleRegisterForm = (e) => setShowRegisterForm(!showRegisterForm);
  const toggleForgottenPasswordForm = (e) => setShowForgottenPasswordForm(!showForgottenPasswordForm);
  const toggleResetPasswordForm = (e) => setShowResetPasswordForm(!showResetPasswordForm);
  const toggleConfirmLoginPopup = (e) => setShowConfirmLoginPopup(!showConfirmLoginPopup);

  useEffect(() => {
    // Start the welcome text animation on mount — letters fade then rise
    if (welcomeRef.current) {
      // letter timing tuned in helper; when complete, sequence: logo fade -> logo rise -> panels fade -> panels rise
      animateReveal(welcomeRef.current, 'WELCOME TO', { interval: 20, riseDelay: 160 }).then(() => {
        // 1) show logo (fade in)
        setLogoPhase('visible');

        // logo timing should match CSS: logoFadeMs and logoRiseMs
        const logoFadeMs = 300; // twice as fast
        const logoRiseMs = 175;  // twice as fast

        // start logo rise at ~50% of fade
        const logoRiseStart = Math.round(logoFadeMs / 2);
        setTimeout(() => {
          setLogoPhase('risen');

          // 2) shortly after logo has begun its rise, show panels (fade only)
          const panelsVisibleDelayAfterLogoRise = 30; // very small delay
          setTimeout(() => {
            setPanelsPhase('visible');
            // panelsPhase 'risen' is retained for API compatibility
            const panelFadeMs = 1200; // restore longer panels fade duration
            const panelRiseStart = Math.round(panelFadeMs / 2);
            setTimeout(() => setPanelsPhase('risen'), panelRiseStart);
          }, panelsVisibleDelayAfterLogoRise);

        }, logoRiseStart);
      });
    }
  }, []);

  return (
    <>
      <div className={"login-container fill-vh" + (logoPhase ? ` intro-logo-${logoPhase}` : '') + (panelsPhase ? ` intro-panels-${panelsPhase}` : '')}>
        <video className="login-vid" autoPlay muted loop id="twitchDemoVid">
          <source src={assets.LoginVideoBackground} type="video/mp4" />
        </video>

        <div className="row fill-height">
            <div className="login-title-section">
            <h1 className="login-main-title" aria-label="WELCOME TO">
              <span className="welcome-text" ref={welcomeRef} />
              <video className="login-icast-logo-inline" autoPlay loop muted preload="auto" src={assets.icastAnimatedLogo} alt="ICAST Logo" />
            </h1>
            <p className="login-subtitle">What will you create today?</p>
          </div>

          <div className="login-inner">
            <div className="login-left">

              <div className="login-panel">
                <div className="login-panel-title">Existing users - Login</div>
                <a href={twitchAuthHref} className="twitch-link">
                  <button
                    type="button"
                    className="submit-btn twitch-btn cursor-pointer"
                    onClick={(e) => {
                      // Defensive: ensure navigation happens even if anchor click is intercepted
                      try { console.log('Twitch login click, navigating to', twitchAuthHref); } catch (err) {}
                      window.location.href = twitchAuthHref;
                    }}
                  >
                    Login With Twitch
                  </button>
                </a>
                <div className="login-form-divider">
                  <span className="login-form-divider-line"><hr></hr></span>
                  <span className="login-form-divider-text">OR</span>
                  <span className="login-form-divider-line"><hr></hr></span>
                </div>
                <form id="loginform" className="form">
                  <div className="form-group login-input-wrapper">
                    <FontAwesomeIcon icon={faUser} className="login-input-icon" />
                    <FormField
                      type="text"
                      id="username"
                      placeholder="Username"
                      value={username}
                      setFunc={setUsername}
                      label={""}
                      error={usernameError}
                    />
                  </div>
                  <div className="form-group login-input-wrapper">
                    <FontAwesomeIcon icon={faLock} className="login-input-icon" />
                    <FormField
                      type="password"
                      id="password"
                      placeholder="Password"
                      setFunc={setPassword}
                      label={""}
                      error={passwordError}
                    />
                  </div>

                  <div className="form-row">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label htmlFor="remember-me">Remember me</label>
                    </div>
                    <span className="forgotten-password-link" onClick={toggleForgottenPasswordForm}>Forgot Password?</span>
                  </div>

                  <button type="button" className={'submit-btn' + (isSubmitting ? ' button-inactive' : '')} onClick={isSubmitting ? null : loginFormSubmitHandler}>
                    {isSubmitting ? "LOGGING IN" : "LOGIN"}
                  </button>
                </form>
              </div>
            </div>

            <div className="login-right-side">
              <div className="create-account-panel">
                <h3 className="create-title">New to ICAST?</h3>
                <div className="create-avatar">
                  <img src={assets.DefaultCreateAvatar || assets.BlankUser} alt="Create avatar" />
                </div>
                {/* create-copy paragraph removed per design request */}
                <button type="button" className="submit-btn get-started" onClick={toggleRegisterForm}>GET STARTED</button>
              </div>
            </div>
          </div>
        </div>
        {/* PoweredByTwitch animation/video pinned to bottom center */}
        <video id="poweredByTwitchVid" className="powered-by-video" autoPlay muted loop preload="auto">
          <source src={assets.PoweredByTwitch} type="video/webm" />
        </video>
      </div>

      {showRegisterForm && <Register email={registerEmail} closePanelFunc={toggleRegisterForm} />}
      {showForgottenPasswordForm && <ForgottenPassword closePanelFunc={toggleForgottenPasswordForm} />}
      {showResetPasswordForm && <ResetPassword resetToken={resetToken} resetEmail={resetEmail} closePanelFunc={toggleResetPasswordForm} />}
      {authContext.isLoggedIn && !authContext.isConfirmed && <ConfirmLogin closePanelFunc={toggleConfirmLoginPopup} /> }
    </>
  );
};

export default Login;
